import React, { useState, useEffect } from 'react';
// import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Download, AlertCircle, CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
// import { createPageUrl } from '@/utils';
import { Toaster, toast } from "sonner";
import { useAuth } from '../context/AuthContext';
// import TermsModal from '@/components/dashboard/TermsModal';
// import * as userApi from '@/components/api/userApi';
// import * as adminApi from '@/components/api/adminApi';

// -- Mock API placeholders until actual endpoints are available --
const adminApi = {
    getAllFolders: async () => [
        { id: '1', folder_id: '1', name: 'Gold Scalp', code: 'GOLD_SC', is_active: true },
        { id: '2', folder_id: '2', name: 'Major Pairs', code: 'MAJORS', is_active: true },
        { id: '3', folder_id: '3', name: 'Indices', code: 'INDICES', is_active: false }
    ],
    ingestAlert: async (alertData) => {
        // Mock ingestion
        console.log('Ingesting alert:', alertData);
        // Simulate random failure for testing logic
        if (Math.random() < 0.1) throw new Error("Random ingestion error for testing");
        return { success: true };
    }
};

const userApi = {
    getTermsStatus: async (id) => ({ accepted: true, terms_version_id: 'v2', terms_content: "Terms here..." }),
    acceptTerms: async (id, version) => true
};
// ------------------------------------------------

export default function AlertReceiver() {
    const { user, loading: authLoading } = useAuth();
    const [userLoading, setUserLoading] = useState(true);
    const [termsAccepting, setTermsAccepting] = useState(false);
    const [termsStatus, setTermsStatus] = useState(null);
    const [apiUrl, setApiUrl] = useState('');
    const [isFetching, setIsFetching] = useState(false);
    const [lastFetchResult, setLastFetchResult] = useState(null);
    const [testJson, setTestJson] = useState(JSON.stringify({
        "indicator": "TRX",
        "system": "SFT",
        "pair": "XAUUSD",
        "timeframe": "M1",
        "signal": "BUY",
        "entry": 4469.445,
        "sl": 4468.487,
        "tp": [4470.760, 4472.075, 4473.391, 4474.706],
        "time": "2026-01-07 05:23",
        "text": "[TRX] BUY SIGNAL | XAUUSD | TF M1\n🟢 Entry: 4469.445\n❌ SL: 4468.487\n🥅 TP Mandatory: 4470.760\n🎯 TP 2: 4472.075\n🎯 TP 3: 4473.391\n🎯 TP 4: 4474.706\n📆 Time: 2026-01-07 05:23"
    }, null, 2));
    const [isIngesting, setIsIngesting] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        // Simulate user loading
        if (!authLoading && user) {
            setUserLoading(false);
            setTermsStatus({ accepted: true });
        } else if (!authLoading && !user) {
            setUserLoading(false);
        }
    }, [user, authLoading]);

    const hasAcceptedTerms = termsStatus?.accepted === true;
    const isAdmin = user?.role === 'admin';

    const { data: folders = [] } = useQuery({
        queryKey: ['adminFolders'],
        queryFn: () => adminApi.getAllFolders(),
        enabled: !!(hasAcceptedTerms && isAdmin),
    });

    const acceptTerms = async () => {
        setTermsAccepting(true);
        // await userApi.acceptTerms(user.id, termsStatus.terms_version_id);
        setTermsStatus({ ...termsStatus, accepted: true });
        setTermsAccepting(false);
    };

    const fetchAlerts = async () => {
        if (!apiUrl) {
            toast.error('Please enter your API URL');
            return;
        }

        setIsFetching(true);
        setLastFetchResult(null);

        try {
            const response = await fetch(apiUrl);
            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }

            const data = await response.json();
            const alerts = Array.isArray(data) ? data : [data];

            let successCount = 0;
            let errorCount = 0;
            const errors = [];

            for (const alertData of alerts) {
                try {
                    await adminApi.ingestAlert(alertData);
                    successCount++;
                } catch (err) {
                    errorCount++;
                    errors.push(err.message);
                }
            }

            setLastFetchResult({
                success: true,
                message: `Processed ${alerts.length} alert(s): ${successCount} saved, ${errorCount} failed`,
                errors: errors.length > 0 ? errors : null
            });

            if (successCount > 0) {
                toast.success(`${successCount} alert(s) saved successfully`);
            }
            if (errorCount > 0) {
                toast.error(`${errorCount} alert(s) failed`);
            }

        } catch (err) {
            setLastFetchResult({
                success: false,
                message: `Failed to fetch: ${err.message}`
            });
            toast.error('Failed to fetch alerts from API');
        } finally {
            setIsFetching(false);
        }
    };

    const processTestAlert = async () => {
        setIsIngesting(true);
        try {
            const alertData = JSON.parse(testJson);
            await adminApi.ingestAlert(alertData);
            toast.success('Test alert created successfully');
        } catch (err) {
            toast.error(err.message || 'Failed to create test alert');
        } finally {
            setIsIngesting(false);
        }
    };

    if (authLoading || userLoading) {
        return (
            <div className="h-screen bg-slate-900 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
            </div>
        );
    }

    if (!hasAcceptedTerms) {
        return (
            <div className="h-screen bg-slate-900 flex items-center justify-center text-white">
                <div className="text-center">
                    <h2 className="text-xl mb-4">Terms Acceptance Required</h2>
                    <Button onClick={acceptTerms} disabled={termsAccepting}>
                        {termsAccepting ? "Accepting..." : "Accept Mock Terms"}
                    </Button>
                </div>
            </div>
        )
    }

    if (!isAdmin) {
        return (
            <div className="h-screen bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <ShieldAlert className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h1 className="text-xl font-bold text-white mb-2">Access Denied</h1>
                    <p className="text-slate-400 mb-4">Only administrators can access this page.</p>
                    <Link to="/dashboard">
                        <Button className="bg-blue-600 hover:bg-blue-700">Go to Dashboard</Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-900 p-6">
            <Toaster richColors position="top-right" />

            <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-4 mb-8">

                    <div>
                        <h1 className="text-2xl font-bold text-white">Alert Receiver</h1>
                        <p className="text-slate-400 text-sm mt-1">Ingest alerts from your API endpoint</p>
                    </div>
                </div>

                <div className="grid gap-6">
                    <Card className="bg-slate-950 border-slate-800">
                        <CardHeader>
                            <CardTitle className="text-white">Fetch from API</CardTitle>
                            <CardDescription className="text-slate-400">
                                Enter your API URL to fetch and store alerts
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label className="text-slate-300">API Endpoint URL</Label>
                                <div className="flex gap-3">
                                    <Input
                                        value={apiUrl}
                                        onChange={(e) => setApiUrl(e.target.value)}
                                        placeholder="https://your-api.com/alerts"
                                        className="bg-slate-800 border-slate-700 text-white flex-1"
                                    />
                                    <Button
                                        onClick={fetchAlerts}
                                        disabled={isFetching}
                                        className="bg-blue-600 hover:bg-blue-700"
                                    >
                                        {isFetching ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Download className="h-4 w-4" />
                                        )}
                                        <span className="ml-2">Fetch</span>
                                    </Button>
                                </div>
                            </div>

                            {lastFetchResult && (
                                <div className={`p-4 rounded-lg ${lastFetchResult.success ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        {lastFetchResult.success ? (
                                            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                                        ) : (
                                            <AlertCircle className="h-5 w-5 text-red-400" />
                                        )}
                                        <span className={lastFetchResult.success ? 'text-emerald-400' : 'text-red-400'}>
                                            {lastFetchResult.message}
                                        </span>
                                    </div>
                                    {lastFetchResult.errors && (
                                        <ul className="text-sm text-red-300 mt-2 space-y-1">
                                            {lastFetchResult.errors.map((err, i) => (
                                                <li key={i}>• {err}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}

                            <div className="pt-4 border-t border-slate-800">
                                <p className="text-sm text-slate-500">
                                    <strong>Note:</strong> Alerts are always stored regardless of folder status.
                                    Users will only see alerts from folders they follow.
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-slate-950 border-slate-800">
                        <CardHeader>
                            <CardTitle className="text-white">Manual Test Alert</CardTitle>
                            <CardDescription className="text-slate-400">
                                Paste JSON to manually create a test alert
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label className="text-slate-300">Alert JSON</Label>
                                <Textarea
                                    value={testJson}
                                    onChange={(e) => setTestJson(e.target.value)}
                                    className="bg-slate-800 border-slate-700 text-white font-mono text-sm h-64"
                                />
                            </div>
                            <Button
                                onClick={processTestAlert}
                                disabled={isIngesting}
                                className="bg-emerald-600 hover:bg-emerald-700"
                            >
                                {isIngesting ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : null}
                                Create Test Alert
                            </Button>
                        </CardContent>
                    </Card>

                    <Card className="bg-slate-950 border-slate-800">
                        <CardHeader>
                            <CardTitle className="text-white">Available Folders</CardTitle>
                            <CardDescription className="text-slate-400">
                                Alerts are mapped to folders by the "system" field matching folder "code"
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-wrap gap-2">
                                {folders.length === 0 ? (
                                    <p className="text-slate-500">No folders created yet. Create folders in the Admin page first.</p>
                                ) : (
                                    folders.map(folder => (
                                        <Badge
                                            key={folder.id}
                                            className={`px-3 py-1 ${folder.is_active
                                                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                                : 'bg-slate-700 text-slate-400 border border-slate-600'
                                                }`}
                                        >
                                            {folder.name} ({folder.code})
                                            {!folder.is_active && ' - Inactive'}
                                        </Badge>
                                    ))
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
